import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommissionRates = ({ rateStructures, currentUserTerritory, onStatementGenerate }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorAmount, setCalculatorAmount] = useState('');
  const [calculatorTerritory, setCalculatorTerritory] = useState(currentUserTerritory);

  const tabs = [
    { id: 'current', label: 'Current Rates', icon: 'DollarSign' },
    { id: 'historical', label: 'Rate History', icon: 'History' },
    { id: 'calculator', label: 'Rate Calculator', icon: 'Calculator' }
  ];

  const calculateCommission = (amount, territory) => {
    const structure = rateStructures?.find(s => s?.territory === territory);
    if (!structure) return 0;

    const numAmount = parseFloat(amount) || 0;
    
    // Find applicable tier rate
    let rate = structure?.baseRate;
    for (const tier of structure?.tierRates) {
      const [min, max] = tier?.volume?.split('-')?.map(v => 
        v?.includes('+') ? parseFloat(v?.replace('+', '')) : parseFloat(v?.replace(',', ''))
      );
      
      if (max && numAmount >= min && numAmount <= max) {
        rate = tier?.rate;
        break;
      } else if (!max && numAmount >= min) {
        rate = tier?.rate;
        break;
      }
    }

    return (numAmount * rate) / 100;
  };

  const getRateColor = (rate, baseRate) => {
    if (rate > baseRate) return 'text-success';
    if (rate < baseRate) return 'text-warning';
    return 'text-foreground';
  };

  const handleStatementGenerate = (period) => {
    onStatementGenerate(period);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Commission Rate Tables</h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleStatementGenerate('current-month')}
            iconName="Download"
            iconPosition="left"
            iconSize={14}
          >
            Statement
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-background rounded-lg p-1">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Current Rates Tab */}
      {activeTab === 'current' && (
        <div className="space-y-6">
          {rateStructures?.map((structure) => (
            <div
              key={structure?.territory}
              className={`bg-background border rounded-lg p-4 ${
                structure?.territory === currentUserTerritory 
                  ? 'border-primary ring-2 ring-primary/10' :'border-border'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <h4 className="font-semibold text-foreground">{structure?.territory}</h4>
                  {structure?.territory === currentUserTerritory && (
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20">
                      Your Territory
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Base Rate:</span>
                  <span className="font-semibold text-primary">{structure?.baseRate}%</span>
                  {structure?.bonusEligible && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-warning" />
                      <span className="text-xs text-warning">Bonus Eligible</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tier Structure */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground">Volume Range</th>
                      <th className="text-right py-2 text-muted-foreground">Rate</th>
                      <th className="text-right py-2 text-muted-foreground">Tier Bonus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structure?.tierRates?.map((tier, index) => (
                      <tr key={index} className="border-b border-border/50">
                        <td className="py-2 text-foreground">${tier?.volume}</td>
                        <td className={`py-2 text-right font-medium ${getRateColor(tier?.rate, structure?.baseRate)}`}>
                          {tier?.rate}%
                        </td>
                        <td className="py-2 text-right text-muted-foreground">
                          {tier?.rate > structure?.baseRate ? 
                            `+${(tier?.rate - structure?.baseRate)?.toFixed(2)}%` : 
                            '-'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Performance Bonus Info */}
              {structure?.bonusEligible && (
                <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="Star" size={16} className="text-warning" />
                    <p className="text-sm text-warning font-medium">Performance Bonus Available</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Additional 10% bonus on commissions when monthly volume exceeds tier requirements
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Rate Calculator Tab */}
      {activeTab === 'calculator' && (
        <div className="space-y-6">
          <div className="bg-background border border-border rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-4">Commission Calculator</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Transaction Amount ($)
                </label>
                <input
                  type="number"
                  value={calculatorAmount}
                  onChange={(e) => setCalculatorAmount(e?.target?.value)}
                  placeholder="Enter amount..."
                  step="0.01"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Territory
                </label>
                <select
                  value={calculatorTerritory}
                  onChange={(e) => setCalculatorTerritory(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {rateStructures?.map((structure) => (
                    <option key={structure?.territory} value={structure?.territory}>
                      {structure?.territory}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {calculatorAmount && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Commission</p>
                    <p className="text-2xl font-bold text-primary">
                      ${calculateCommission(calculatorAmount, calculatorTerritory)?.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      Applied Rate: {
                        rateStructures?.find(s => s?.territory === calculatorTerritory)?.baseRate
                      }%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      On ${parseFloat(calculatorAmount)?.toLocaleString()} transaction
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Historical Rates Tab */}
      {activeTab === 'historical' && (
        <div className="space-y-4">
          <div className="text-center py-8">
            <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Rate history will be available soon</p>
            <p className="text-sm text-muted-foreground">
              Track changes in commission rates over time
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionRates;